import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { History, Trophy, ArrowUp } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * useLastSessionsHints — shared cache hook for the editor + active workout.
 *
 * Loads { exerciseId: { sessions, pr, suggestion } } in a single batched call.
 * Cache key is `${clientId}:${sortedExerciseIds.join(',')}`.
 */
const _cache = new Map(); // module-level cache (cleared on full page reload)
const _CACHE_MAX = 50; // bound size — see note from iteration_59 testing agent

export const useLastSessionsHints = (clientId, exerciseIds, limit = 3) => {
  const [data, setData] = useState({});
  const [loading, setLoading] = useState(false);

  const fetchHints = useCallback(async () => {
    if (!clientId || !exerciseIds || exerciseIds.length === 0) {
      setData({});
      return;
    }
    // Stable cache key
    const ids = [...new Set(exerciseIds.filter(Boolean))].sort();
    if (ids.length === 0) {
      setData({});
      return;
    }
    const cacheKey = `${clientId}:${limit}:${ids.join(',')}`;
    if (_cache.has(cacheKey)) {
      setData(_cache.get(cacheKey));
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(
        `${API}/training/last-sessions/${clientId}?exercise_ids=${ids.join(',')}&limit=${limit}`
      );
      const payload = res.data || {};
      // Bound cache size — drop oldest insertion if over the limit
      if (_cache.size >= _CACHE_MAX) {
        const oldest = _cache.keys().next().value;
        if (oldest) _cache.delete(oldest);
      }
      _cache.set(cacheKey, payload);
      setData(payload);
    } catch (e) {
      // Silent fail — hints are optional, don't disrupt workout
      setData({});
    } finally {
      setLoading(false);
    }
  }, [clientId, exerciseIds, limit]);

  useEffect(() => { fetchHints(); }, [fetchHints]);

  return { data, loading, refetch: fetchHints };
};

/**
 * Helper: compact summary "92.5kg × 16, 8, 7, 8 (RPE 9)"
 */
const formatSetsCompact = (sets) => {
  if (!sets || sets.length === 0) return '—';
  // Group by weight for readability when all weights match
  const allSameWeight = sets.every((s) => s.weight === sets[0].weight);
  const rpe = sets.map((s) => s.rpe).filter((v) => v != null);
  const maxRpe = rpe.length ? Math.max(...rpe) : null;
  if (allSameWeight) {
    const reps = sets.map((s) => s.reps).join(',');
    const w = sets[0].weight;
    const wPart = w > 0 ? `${w}kg × ` : '';
    return `${wPart}${reps}${maxRpe != null ? ` · RPE ${maxRpe}` : ''}`;
  }
  // Mixed weights — show each
  return sets
    .map((s) => `${s.weight}×${s.reps}${s.rpe ? `@${s.rpe}` : ''}`)
    .join(', ');
};

/**
 * Helper: best set from a list (heaviest weight, tiebreak on reps).
 */
export const bestSetOf = (sets) => {
  let best = null;
  for (const s of sets || []) {
    if (!s) continue;
    const w = s.weight || 0;
    const r = s.reps || 0;
    if (!best || w > best.weight || (w === best.weight && r > best.reps)) {
      best = { weight: w, reps: r, rpe: s.rpe };
    }
  }
  return best;
};

/**
 * Compare current logged sets to a reference best (last-session best_set).
 * Returns one of: 'up' | 'same' | 'down' | null (when no comparison possible).
 *
 * The signal is intentionally simple — best-set vs best-set:
 *   - heavier weight, OR same weight with more reps  → 'up'
 *   - lighter weight, OR same weight with fewer reps → 'down'
 *   - exact match                                    → 'same'
 *
 * Used to render the per-exercise improvement indicator (↑ / → / ↓) and to
 * trigger the on-set "improvement check" animation.
 */
export const compareToReference = (currentSets, referenceBest) => {
  if (!referenceBest) return null;
  const cur = bestSetOf(currentSets);
  if (!cur || cur.reps === 0) return null;
  const cw = cur.weight || 0;
  const cr = cur.reps || 0;
  const rw = referenceBest.weight || 0;
  const rr = referenceBest.reps || 0;
  if (cw > rw) return 'up';
  if (cw < rw) return 'down';
  // same weight
  if (cr > rr) return 'up';
  if (cr < rr) return 'down';
  return 'same';
};

const DIRECTION_META = {
  up:   { icon: '↑', label: 'Mejorando', cls: 'bg-green-500/20 text-green-200 border-green-400/30' },
  same: { icon: '→', label: 'Igualando', cls: 'bg-blue-500/20 text-blue-200 border-blue-400/30' },
  down: { icon: '↓', label: 'Por debajo', cls: 'bg-red-500/20 text-red-200 border-red-400/30' },
};

/**
 * <ImprovementBadge />
 * Tiny pill rendering ↑/→/↓ based on a `direction` value.
 * Returns null when direction is unknown.
 */
export const ImprovementBadge = ({ direction, compact = false }) => {
  if (!direction || !DIRECTION_META[direction]) return null;
  const meta = DIRECTION_META[direction];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-semibold ${meta.cls}`}
      data-testid={`improvement-badge-${direction}`}
      title={meta.label}
    >
      <span className="text-base leading-none">{meta.icon}</span>
      {!compact && <span>{meta.label}</span>}
    </span>
  );
};

const formatRelativeDate = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso + 'T12:00:00');
    const today = new Date();
    today.setHours(12, 0, 0, 0);
    const diffDays = Math.round((today - d) / (1000 * 60 * 60 * 24));
    if (diffDays === 0) return 'hoy';
    if (diffDays === 1) return 'ayer';
    if (diffDays < 7) return `hace ${diffDays}d`;
    if (diffDays < 30) return `hace ${Math.round(diffDays / 7)}sem`;
    return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short' });
  } catch { return ''; }
};

/**
 * <LastSessionsHint />
 *
 * Two render modes:
 *   variant="compact"  → single inline line (used by FullScreenWorkoutEditor)
 *   variant="full"     → dual-block "Última sesión" + "Mejor marca" (used by ActiveWorkoutInterface)
 *
 * The component is purely presentational — fetch the data with `useLastSessionsHints`
 * higher up and pass `hint` in.
 */
const LastSessionsHint = ({ hint, variant = 'compact', exerciseName }) => {
  if (!hint) return null;
  const sessions = hint.sessions || [];
  const pr = hint.pr;
  const suggestion = hint.suggestion;

  // Pick the reference session for "last": most recent non-deload, fall back to most recent
  const reference = sessions.find((s) => !s.is_deload) || sessions[0] || null;

  if (!reference && !pr) {
    if (variant === 'full') {
      return (
        <div
          className="text-xs text-blue-300/60 italic mb-2"
          data-testid="last-sessions-empty"
        >
          Sin historial previo de este ejercicio
        </div>
      );
    }
    return null;
  }

  if (variant === 'compact') {
    // EDITOR MODE — single line chip
    return (
      <div
        className="flex flex-wrap items-center gap-2 text-xs text-blue-200 bg-blue-900/30 border border-blue-400/20 rounded-md px-2 py-1 mt-1"
        data-testid="last-sessions-hint-compact"
      >
        <History className="w-3 h-3 text-blue-300 flex-shrink-0" />
        {reference ? (
          <>
            <span className="text-blue-300">Última:</span>
            <span className="font-mono font-medium">
              {formatSetsCompact(reference.sets)}
            </span>
            <span className="text-blue-400/70">
              · {formatRelativeDate(reference.date)}
            </span>
            {reference.is_deload && (
              <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 text-[10px]">
                🪶 descarga
              </span>
            )}
          </>
        ) : (
          <span className="text-blue-300/60">Sin historial</span>
        )}
        {pr && (
          <span className="ml-auto text-yellow-300/90 font-mono">
            ⭐ {pr.weight}kg × {pr.reps}
          </span>
        )}
      </div>
    );
  }

  // FULL MODE — active workout, dual-block + suggestion
  return (
    <div
      className="space-y-2 mb-3"
      data-testid="last-sessions-hint-full"
      aria-label={`Last session and PR for ${exerciseName || 'exercise'}`}
    >
      {/* Última sesión */}
      {reference && (
        <div
          className="bg-blue-500/15 border border-blue-400/30 rounded-lg px-3 py-2"
          data-testid="last-session-block"
        >
          <div className="flex items-center gap-2 mb-0.5">
            <History className="w-4 h-4 text-blue-300" />
            <span className="text-blue-200 text-xs font-semibold uppercase tracking-wide">
              Última sesión
            </span>
            <span className="text-blue-400/70 text-xs">
              · {formatRelativeDate(reference.date)}
            </span>
            {reference.is_deload && (
              <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-200 text-[10px]">
                🪶 descarga
              </span>
            )}
          </div>
          <div className="text-blue-50 font-mono font-bold text-lg" data-testid="last-session-sets">
            {(() => {
              const sets = reference.sets || [];
              if (sets.length === 0) return '—';
              const allSame = sets.every((s) => s.weight === sets[0].weight);
              const bestNum = reference.best_set?.set_number;
              const wPrefix = allSame && sets[0].weight > 0 ? `${sets[0].weight}kg × ` : '';
              return (
                <span className="inline-flex flex-wrap items-baseline gap-x-2 gap-y-1">
                  {wPrefix && <span className="text-blue-200/90">{wPrefix}</span>}
                  {sets.map((s, i) => {
                    const isBest = bestNum === i + 1;
                    const body = allSame
                      ? `${s.reps}`
                      : `${s.weight > 0 ? `${s.weight}kg×` : ''}${s.reps}`;
                    return (
                      <span
                        key={i}
                        className={`inline-flex items-baseline gap-0.5 ${isBest ? 'text-yellow-200' : ''}`}
                      >
                        <span className="text-[10px] font-semibold text-blue-300/70">#{i + 1}</span>
                        <span>{body}{s.rpe != null ? `@${s.rpe}` : ''}</span>
                      </span>
                    );
                  })}
                </span>
              );
            })()}
          </div>
          {suggestion && !reference.is_deload && (
            <div className="flex items-center gap-1 text-blue-200/90 text-xs mt-1">
              <ArrowUp className="w-3 h-3 text-green-300" />
              <span>{suggestion}</span>
            </div>
          )}
        </div>
      )}

      {/* Mejor marca (PR) */}
      {pr && (
        <div
          className="bg-yellow-500/15 border border-yellow-400/30 rounded-lg px-3 py-2"
          data-testid="best-set-block"
        >
          <div className="flex items-center gap-2 mb-0.5">
            <Trophy className="w-4 h-4 text-yellow-300" />
            <span className="text-yellow-200 text-xs font-semibold uppercase tracking-wide">
              Mejor marca
            </span>
            <span className="text-yellow-400/70 text-xs">
              · {formatRelativeDate(pr.date)}
            </span>
            {pr.set_number != null && (
              <span className="text-yellow-400/70 text-xs" data-testid="best-set-number">
                · serie #{pr.set_number}
              </span>
            )}
          </div>
          <div className="text-yellow-50 font-mono font-bold text-lg">
            {pr.weight}kg × {pr.reps}
            {pr.rpe != null && (
              <span className="text-yellow-300/80 text-sm font-normal ml-2">
                RPE {pr.rpe}
              </span>
            )}
          </div>
          <div className="text-yellow-200/70 text-xs mt-0.5 italic">
            Referencia máxima
          </div>
        </div>
      )}
    </div>
  );
};

export default LastSessionsHint;
